import React from "react";
import styled from "styled-components";
import { colors, fonts } from "./mixins";
import { Minus, Plus, Trash } from "../icons";

interface QuantitySpinnerProps {
  quantity: number;
  onIncrease: () => void;
  onDecrease: () => void;
  onDelete: () => void;
  min?: number;
  max?: number;
}

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SpinnerControls = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #edf1f6;
  border-radius: 5.33px;
  overflow: hidden;
  gap: 4px;
`;

const SpinnerButton = styled.button`
  display: flex;
  height: 32px;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  transition: background 0.15s ease;
  padding: 0;

  @media (hover: hover) {
    &:not(:disabled):hover {
      background: #F9FBFC;
    }
  }

  &:not(:disabled):active {
    background: #F9FBFC;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  // Keyboard focus recolors the icon (it strokes with currentColor) and tints
  // the cell like hover, instead of drawing an outline box.
  &:focus-visible {
    outline: none;
    color: ${colors.primaryButton};
    background: #F9FBFC;
  }

  &:disabled {
    color: ${colors.lightGray};
    cursor: default;
  }
`;

const QuantityDisplay = styled.span`
  min-width: 32px;
  text-align: center;
  ${fonts.normalTextBold()};
`;

const Separator = styled.div`
  width: 1px;
  height: 32px;
  background-color: #edf1f6;
`;

const DeleteButton = styled.button`
  display: flex;
  border: none;
  padding: 0;
  background: transparent;
  color: #0D0C1C;
  cursor: pointer;
  transition: color 0.15s ease;

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: none;
    color: ${colors.primaryButton};
  }
`;

export function QuantitySpinner({
  quantity,
  onIncrease,
  onDecrease,
  onDelete,
  min,
  max,
}: QuantitySpinnerProps) {
  return (
    <SpinnerWrapper id="spinner">
      <SpinnerControls>
        <SpinnerButton
          type="button"
          onClick={onDecrease}
          disabled={quantity <= min}
          aria-label="Decrease quantity"
        >
          <Minus />
          <Separator />
        </SpinnerButton>

        <QuantityDisplay id="quantity">{quantity}</QuantityDisplay>

        <SpinnerButton
          type="button"
          onClick={onIncrease}
          disabled={quantity >= max}
          aria-label="Increase quantity"
        >
          <Separator />
          <Plus />
        </SpinnerButton>
      </SpinnerControls>
      <DeleteButton type="button" onClick={onDelete} aria-label="Remove item">
        <Trash />
      </DeleteButton>
    </SpinnerWrapper>
  );
}
